import React, { useState, useEffect } from 'react';
import { AppNav } from '../../components/layout/AppNav';
import { adminService, type AdminProduct } from '../../services/admin.service';
import { formatSAR } from '../../utils/formatters';
import { useToast } from '../../components/ui/Toast';

const CATEGORIES = ['5G', 'Fixed_BBI', 'Fixed_BDI', 'Mobility'];
const CATEGORY_LABELS: Record<string, string> = {
  '5G': '5G Fixed',
  Fixed_BBI: 'Fixed Broadband',
  Fixed_BDI: 'Fixed Dedicated',
  Mobility: 'Mobility',
};

type ModalMode = 'create' | 'edit' | null;

const emptyForm = (): Partial<AdminProduct> => ({
  category: '5G',
  name: '',
  description: '',
  price: 0,
  vatRate: 0.15,
  requiresCoverage: true,
  isBestseller: false,
  isActive: true,
  sortOrder: 0,
  setupFee: null,
  cancellationFee: null,
  installationTimeline: '',
  contractTerms: '',
  configSchema: null,
});

export const AdminProductsPage: React.FC = () => {
  const { showToast } = useToast();
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalMode>(null);
  const [editTarget, setEditTarget] = useState<AdminProduct | null>(null);
  const [form, setForm] = useState<Partial<AdminProduct>>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AdminProduct | null>(null);
  const [filterCategory, setFilterCategory] = useState('');

  const refresh = () => {
    adminService.getProducts().then(setProducts).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const openCreate = () => {
    setForm(emptyForm());
    setEditTarget(null);
    setModal('create');
  };

  const openEdit = (p: AdminProduct) => {
    setEditTarget(p);
    setForm({ ...p });
    setModal('edit');
  };

  const buildPayload = (): Partial<AdminProduct> => {
    const p = { ...form } as Record<string, unknown>;
    delete p['id'];
    delete p['createdAt'];
    delete p['updatedAt'];
    delete p['configSchema'];
    return p as Partial<AdminProduct>;
  };

  const handleCreate = () => {
    const payload = buildPayload();
    if (!payload.name) { showToast('Name is required', 'error'); return; }
    setSaving(true);
    adminService.createProduct(payload)
      .then(() => { showToast('Product created', 'success'); setModal(null); refresh(); })
      .catch((e) => showToast(e?.response?.data?.message ?? 'Failed to create product', 'error'))
      .finally(() => setSaving(false));
  };

  const handleEdit = () => {
    if (!editTarget) return;
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    adminService.updateProduct(editTarget.id, payload)
      .then(() => { showToast('Product updated', 'success'); setModal(null); refresh(); })
      .catch(() => showToast('Failed to update product', 'error'))
      .finally(() => setSaving(false));
  };

  const handleDelete = (p: AdminProduct) => {
    adminService.deleteProduct(p.id)
      .then(() => { showToast('Product deactivated', 'success'); refresh(); })
      .catch(() => showToast('Failed to deactivate product', 'error'));
    setConfirmDelete(null);
  };

  const toggleBestseller = (p: AdminProduct) => {
    adminService.updateProduct(p.id, { isBestseller: !p.isBestseller })
      .then(() => refresh())
      .catch(() => showToast('Failed to update', 'error'));
  };

  const toggleActive = (p: AdminProduct) => {
    adminService.updateProduct(p.id, { isActive: !p.isActive })
      .then(() => refresh())
      .catch(() => showToast('Failed to update', 'error'));
  };

  const visible = filterCategory ? products.filter((p) => p.category === filterCategory) : products;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <AppNav title="Product Catalog" subtitle={`${products.length} products`} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setFilterCategory('')} style={{ ...chipStyle, background: !filterCategory ? '#0f172a' : '#fff', color: !filterCategory ? '#fff' : '#374151' }}>All</button>
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setFilterCategory(c)} style={{ ...chipStyle, background: filterCategory === c ? '#0f172a' : '#fff', color: filterCategory === c ? '#fff' : '#374151' }}>{CATEGORY_LABELS[c] ?? c}</button>
            ))}
          </div>
          <button onClick={openCreate} style={btnPrimaryStyle}>+ Add Product</button>
        </div>

        <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e9ecef', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                  <Th>Name</Th>
                  <Th>Category</Th>
                  <Th>Price/mo</Th>
                  <Th>Setup Fee</Th>
                  <Th>Most Popular</Th>
                  <Th>Active</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: p.isActive ? 1 : 0.5 }}>
                    <Td>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      {p.description && <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>{p.description.slice(0, 60)}{p.description.length > 60 ? '…' : ''}</div>}
                    </Td>
                    <Td><span style={{ fontSize: '12px', background: '#f1f5f9', borderRadius: '4px', padding: '2px 7px' }}>{CATEGORY_LABELS[p.category] ?? p.category}</span></Td>
                    <Td>{formatSAR(p.price)}</Td>
                    <Td>{p.setupFee != null ? formatSAR(p.setupFee) : '—'}</Td>
                    <Td>
                      <button onClick={() => toggleBestseller(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }} title="Toggle most popular">
                        {p.isBestseller ? '⭐' : '☆'}
                      </button>
                    </Td>
                    <Td>
                      <button onClick={() => toggleActive(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }} title="Toggle active">
                        {p.isActive ? '✅' : '⬜'}
                      </button>
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => openEdit(p)} style={btnSecondarySmall}>Edit</button>
                        <button onClick={() => setConfirmDelete(p)} style={{ ...btnSecondarySmall, color: '#dc2626', borderColor: '#fecaca' }}>Remove</button>
                      </div>
                    </Td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>No products</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create / Edit modal */}
      {(modal === 'create' || modal === 'edit') && (
        <Modal title={modal === 'create' ? 'Add Product' : `Edit ${editTarget?.name}`} onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={labelStyle}>Category</label>
              <select value={form.category ?? '5G'} onChange={(e) => setForm({ ...form, category: e.target.value as any })} style={{ ...inputStyle, width: '100%' }}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <FormField label="Name" value={form.name ?? ''} onChange={(v) => setForm({ ...form, name: v })} />
            <FormField label="Description" value={form.description ?? ''} onChange={(v) => setForm({ ...form, description: v })} span multiline />
            <FormField label="Monthly Price (SAR)" type="number" value={String(form.price ?? 0)} onChange={(v) => setForm({ ...form, price: Number(v) })} />
            <FormField label="VAT Rate (e.g. 0.15)" type="number" value={String(form.vatRate ?? 0.15)} onChange={(v) => setForm({ ...form, vatRate: Number(v) })} />
            <FormField label="Setup Fee (SAR, optional)" type="number" value={form.setupFee != null ? String(form.setupFee) : ''} onChange={(v) => setForm({ ...form, setupFee: v ? Number(v) : null })} />
            <FormField label="Cancellation Fee (SAR, optional)" type="number" value={form.cancellationFee != null ? String(form.cancellationFee) : ''} onChange={(v) => setForm({ ...form, cancellationFee: v ? Number(v) : null })} />
            <FormField label="Installation Timeline" value={form.installationTimeline ?? ''} onChange={(v) => setForm({ ...form, installationTimeline: v })} />
            <FormField label="Sort Order" type="number" value={String(form.sortOrder ?? 0)} onChange={(v) => setForm({ ...form, sortOrder: Number(v) })} />
            <FormField label="Contract Terms" value={form.contractTerms ?? ''} onChange={(v) => setForm({ ...form, contractTerms: v })} span multiline />
            <div style={{ display: 'flex', gap: '20px', gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.requiresCoverage ?? true} onChange={(e) => setForm({ ...form, requiresCoverage: e.target.checked })} />
                Requires Coverage Check
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isBestseller ?? false} onChange={(e) => setForm({ ...form, isBestseller: e.target.checked })} />
                Most Popular
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isActive ?? true} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                Active
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
            <button onClick={() => setModal(null)} style={btnSecondaryStyle}>Cancel</button>
            <button onClick={modal === 'create' ? handleCreate : handleEdit} disabled={saving} style={btnPrimaryStyle}>
              {saving ? 'Saving…' : (modal === 'create' ? 'Create Product' : 'Save Changes')}
            </button>
          </div>
        </Modal>
      )}

      {/* Confirm deactivate */}
      {confirmDelete && (
        <Modal title="Remove Product" onClose={() => setConfirmDelete(null)}>
          <p style={{ fontSize: '14px', color: '#374151' }}>
            This will deactivate <strong>{confirmDelete.name}</strong>. It will no longer appear in the catalog.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
            <button onClick={() => setConfirmDelete(null)} style={btnSecondaryStyle}>Cancel</button>
            <button onClick={() => handleDelete(confirmDelete)} style={{ ...btnPrimaryStyle, background: '#dc2626' }}>Remove</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const FormField: React.FC<{ label: string; value: string; onChange: (v: string) => void; type?: string; span?: boolean; multiline?: boolean }> = ({ label, value, onChange, type = 'text', span, multiline }) => (
  <div style={{ gridColumn: span ? '1 / -1' : undefined }}>
    <label style={labelStyle}>{label}</label>
    {multiline ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} style={{ ...inputStyle, width: '100%', resize: 'vertical' }} />
    ) : (
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
    )}
  </div>
);

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
    <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '640px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6c757d' }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{children}</th>
);
const Td: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td style={{ padding: '12px 16px', color: '#374151' }}>{children}</td>
);

const chipStyle: React.CSSProperties = { padding: '5px 12px', border: '1px solid #e9ecef', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' };
const inputStyle: React.CSSProperties = { padding: '8px 10px', border: '1px solid #e9ecef', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 500, color: '#374151', marginBottom: '5px' };
const btnPrimaryStyle: React.CSSProperties = { padding: '8px 18px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' };
const btnSecondaryStyle: React.CSSProperties = { padding: '7px 14px', background: '#fff', color: '#374151', border: '1px solid #e9ecef', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' };
const btnSecondarySmall: React.CSSProperties = { padding: '4px 10px', background: '#fff', color: '#374151', border: '1px solid #e9ecef', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit' };
